import type { AreaComponent } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import { useEditAreaDraftStore } from '@/entities/edit-area';
import { useSelectArea } from '../model/use-select-area';
import { AreaListItem } from './area-list-item';

/**
 * 사이드바 '인쇄 영역' 칸의 접이 목록 — 상품의 모든 인쇄 영역을 한 줄씩 보이고, 펼친 줄이 곧 고른 영역이다(한 번에 하나).
 * 접힌 줄을 누르면 고르고 펼치며, 펼친 줄을 다시 누르면 선택을 풀고 접는다. 사진에서 고르거나 풀어도 같이 펼치고 접힌다.
 * 새 인쇄 영역은 도구 줄에서만 만든다(원통은 도구를 누르면 표면 가운데에 기본 크기로 만든다).
 */
export const AreaList = (): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const { select } = useSelectArea();
  const faceKey = state.selection.face;
  const face = state.faces[faceKey];
  const tagOf = (component: AreaComponent): string => {
    if (face?.placements.some((item) => item.componentId === component.id)) return '이 면';
    const elsewhere = state.draft.activeFaces.some(
      (key) =>
        key !== faceKey &&
        (state.faces[key]?.placements.some((item) => item.componentId === component.id) ?? false),
    );
    return elsewhere ? '다른 면만' : '배치 안 됨';
  };
  // 저장 전 확인 목록이 인쇄 영역 문제는 edit-area-components 로, 배치 문제는 edit-area-placements 로 옮겨 온다.
  return (
    <div id="edit-area-components" tabIndex={-1}>
      <div id="edit-area-placements" tabIndex={-1}>
        {state.components.length === 0 ? null : (
          <ul aria-label="인쇄 영역 목록" className="space-y-2">
            {state.components.map((component) => {
              const open = state.selection.componentId === component.id;
              return (
                <AreaListItem
                  key={component.id}
                  component={component}
                  tag={tagOf(component)}
                  open={open}
                  disabled={state.locked}
                  onToggle={() => select(open ? null : component.id)}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
